import React, { useState, useEffect, useRef } from 'react';
import { Box, Flex, Text } from 'rebass';
import styled from 'styled-components';
import theme from 'styled-theming';
import {
  Section, Toggle, Loading, FeatureGate, Dropdown, Divider, ConfirmModal,
} from '../components';
import api from '../api';
import { useAppState, useNotification } from '../hooks';

const textColor = theme('mode', {
  light: (props) => props.theme.colors.deepGrey,
  dark: (props) => props.theme.colors.midLightGrey,
});

const ToggleLabel = styled(Text)`
  color: ${textColor};
`;

function FeatureToggle() {
  const [featureEnabled, setFeatureEnabled] = useState(null);
  const [configData, setConfigData] = useState({ maxRecommendations: 3 });
  const [isSaving, setIsSaving] = useState(false);
  const isMounted = useRef(true);
  const [confirmModalProps, setConfirmModalProps] = useState(null);
  const { accountId: brandId, themeMode, features } = useAppState();
  const { addNotification } = useNotification();

  useEffect(() => {
    async function fetchToggleState() {
      const isFlagEnabled = await api.mavenConfig.getAiAssistWidgetFlag(brandId);
      if (isMounted.current) {
        setFeatureEnabled(isFlagEnabled);
      }
    }

    async function getConfigV2() {
      setIsSaving(true);
      const config = await api.automationLookup.getConfigV2(brandId);
      if (isMounted.current) {
        setConfigData(config);
        setIsSaving(false);
      }
    }

    fetchToggleState();
    getConfigV2();
    return () => {
      isMounted.current = false;
    };
  }, []); // eslint-disable-line

  async function setEnabledAndMakeApiCall(value) {
    if (isMounted.current) {
      setFeatureEnabled(value);
    }
    try {
      await api.mavenConfig.setAiAssistWidgetFlag(brandId, value);
    } catch (error) {
      if (isMounted.current) {
        setFeatureEnabled(featureEnabled);
        addNotification(error, 'error');
      }
    }
  }

  async function handleToggleButtonPressed() {
    const newValue = !featureEnabled;
    if (newValue) {
      setConfirmModalProps({
        show: true,
        detail: 'Recommended Actions only works with Messaging. Please ensure that you have setup messaging before using this feature.',
        onConfirm: async () => {
          await setEnabledAndMakeApiCall(newValue);
          if (isMounted.current) {
            setConfirmModalProps({
              show: false,
            });
          }
        },
        onCancel: () => {
          if (isMounted.current) {
            setConfirmModalProps({
              show: false,
            });
          }
        },
      });
    } else {
      await setEnabledAndMakeApiCall(newValue);
    }
  }

  async function saveConfigV2(changedConfig) {
    if (!isSaving) {
      setIsSaving(true);
      try {
        const { data } = await api.automationLookup.updateConfigV2(brandId, changedConfig);
        setConfigData(data);
      } catch (error) {
        addNotification(error, 'error');
      }
      if (isMounted.current) {
        setIsSaving(false);
      }
    }
  }

  if (!configData) {
    return null;
  }

  return (
    <Box>
      <Section
        title="Enable recommendations"
        subtitle="Enable or disable recommendations for agents"
        data-testid={`feature-toggle-${featureEnabled ? 'enabled' : 'disabled'}`}
      >
        {featureEnabled === null ? <Loading size={24} /> : (
          <Flex alignItems="center">
            <Toggle
              className={themeMode === 'light' ? null : 'dark-theme'}
              icons={false}
              checked={featureEnabled}
              onChange={handleToggleButtonPressed}
              data-testid="feature-toggle"
            />
            <ToggleLabel
              ml={2}
              fontSize={1}
              fontWeight={400}
              color={featureEnabled ? 'success' : 'midGrey'}
              onClick={(event) => event.stopPropagation()}
            >
              {`${featureEnabled ? 'enabled' : 'disabled'}`}
            </ToggleLabel>

            <FeatureGate
              feature="maven.automationlookup.v2"
            >
              {featureEnabled && features?.['maven.feature.showMaxNumberRecommendations']
                  && (
                    <Flex>
                      <Divider />
                      <Dropdown
                        isEditing
                        label="Maximum number of recommendations:"
                        value={configData.maxRecommendations}
                        selectItem={(maxRecommendations) => {
                          saveConfigV2({
                            maxRecommendations,
                          });
                        }}
                        items={[1, 2, 3, 4, 5]
                          .filter((item) => item !== configData.maxRecommendations)}
                        noFreeText
                        flex={1}
                        isSaving={isSaving}
                        tooltip="Changing this setting for automations also changes it for knowledge bases, and vice versa"
                      />
                      {isSaving && <Loading size={12} ml={2} alignItems="center" data-testid="max-recommendations-loading-spinner" />}
                    </Flex>
                  )}
            </FeatureGate>
          </Flex>
        )}
      </Section>
      <ConfirmModal {...confirmModalProps} />
    </Box>
  );
}

export default FeatureToggle;
